import { useEffect, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { CalendarCheck } from 'lucide-react';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { Confirmar } from '../../componentes/sobreposicao';
import { anunciarOcultos } from '../../componentes/sensivel';
import { useToast } from '../../componentes/toast';
import { get, mensagemDeErro, post } from '../../nucleo/api';
import { carregarMais } from '../../nucleo/foco';
import { hojeNoFuso } from '../../componentes/periodo/periodo';
import { data, dataHora, email, nomeParceiro, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { CHAVE } from './dados';
import { COR_ESTADO, type MeuAgendamento, type MinhaAgendaGoogle } from './tipos';

/** Motivos que o retorno do Google traz em `?motivo=` (o resto vira o texto genérico). */
const MOTIVOS = ['negado', 'state', 'troca', 'escopo', 'sem_codigo', 'nao_configurado', 'sessao', 'google', 'ocupado'] as const;
export const CHAVE_MINHA_AGENDA = [CHAVE, 'google', 'minha-conta'] as const;

/** Rodada rrl-9 (ROB9-B1): dia inteiro só com a data, no fuso do workspace, como na ficha. */
export const quando = (a: Pick<MeuAgendamento, 'inicio' | 'dia_inteiro'>, fuso: string) => (a.dia_inteiro ? data(hojeNoFuso(fuso, new Date(a.inicio))) : dataHora(a.inicio));

/** Pedido de 02/11: os agendamentos dos profissionais da pessoa, do mais novo ao mais antigo. */
function HistoricoDeAgendamentos() {
  const s = useSessao();
  const { t } = useTranslation('agenda');
  const { t: tc } = useTranslation();
  const q = useInfiniteQuery({
    queryKey: [...CHAVE_MINHA_AGENDA, 'agendamentos'],
    queryFn: ({ pageParam }) => get<{ itens: MeuAgendamento[]; proximo_cursor: string | null }>('/agenda/google/minha-conta/agendamentos', { limite: 10, cursor: pageParam || undefined }),
    initialPageParam: '',
    getNextPageParam: (u) => u.proximo_cursor ?? undefined,
  });
  const itens = q.data?.pages.flatMap((p) => p.itens) ?? [];
  return (
    <section className="flex min-w-0 flex-col gap-2 border-t border-borda pt-3" data-historico-agendamentos>
      <h3 className="font-medium text-tinta-2">{t('minhaConta.historico')}</h3>
      {q.isPending ? (
        <p className="text-tinta-3">{t('minhaConta.carregando')}</p>
      ) : q.isError && itens.length === 0 ? (
        <p className="text-perigo" role="alert">
          {t('minhaConta.historicoErro')}
        </p>
      ) : itens.length === 0 ? (
        <p className="text-tinta-3">{t('minhaConta.historicoVazio')}</p>
      ) : (
        <ul className="flex min-w-0 flex-col">
          {itens.map((a) => (
            <li key={a.id} className="@container flex min-w-0 flex-wrap items-center justify-between gap-x-3 gap-y-1 border-t border-borda py-2 first:border-t-0">
              {/* rgs-1 VIS-M1: decide pela largura da lista, não da janela (painel estreito da tela dividida). */}
              <div className="w-full min-w-0 @md:w-auto @md:flex-1">
                <p className="break-words text-tinta-1">{textoLivre(a.assunto, a.parceiro_nome) || a.servico_nome || t('minhaConta.semTitulo')}</p>
                {/* Reunião livre (03/11): sem serviço nem cliente, só a data (esta rota não traz os ids, e o nome nulo com serviço é de outra empresa). */}
                <p className="break-words text-xs text-tinta-3">{a.parceiro_id === null ? quando(a, s.workspace.fuso) : `${quando(a, s.workspace.fuso)} · ${nomeParceiro(a.parceiro_nome)}`}</p>
              </div>
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                {a.no_google && a.estado !== 'cancelado' && <Selo cor="neutro">{t('minhaConta.noGoogle')}</Selo>}
                <Selo cor={COR_ESTADO[a.estado]}>{t(`estados.${a.estado}`)}</Selo>
              </div>
            </li>
          ))}
        </ul>
      )}
      {q.isError && itens.length > 0 && (
        <p className="text-perigo" role="alert">
          {t('minhaConta.historicoErro')}
        </p>
      )}
      {q.hasNextPage && (
        <div className="flex justify-center">
          <Botao variante="secundario" onClick={(e) => carregarMais(() => q.fetchNextPage(), e.currentTarget)} carregando={q.isFetchingNextPage}>
            {tc('comum.carregarMais')}
          </Botao>
        </div>
      )}
    </section>
  );
}

/**
 * Lista 30/10 B (item 34): Minha conta > Perfil > Google Agenda. A pessoa conecta o próprio Google Agenda (OAuth dela) e o
 * profissional ligado a ela passa a usar a agenda principal da conta; "Desconectar" apaga o token e o revoga no Google. Só a
 * sessão da própria pessoa: na simulação o cartão mostra o estado sem os botões (a API também recusa).
 */
export function CartaoMinhaAgendaGoogle() {
  const { t } = useTranslation('agenda');
  const s = useSessao();
  const toast = useToast();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [confirmando, setConfirmando] = useState(false);
  const emSimulacao = !!s.simulacao;
  const q = useQuery({ queryKey: CHAVE_MINHA_AGENDA, queryFn: () => get<MinhaAgendaGoogle>('/agenda/google/minha-conta'), staleTime: 30_000 });

  // Volta do Google: o resultado vem na URL (`?google=conectado` ou `?google=erro&motivo=...`); avisa uma vez e limpa.
  const volta = params.get('google');
  const motivo = params.get('motivo');
  useEffect(() => {
    if (!volta) return;
    if (volta === 'conectado') toast.sucesso(t('minhaConta.conectado'));
    else toast.erro(t(`minhaConta.motivos.${MOTIVOS.includes(motivo as (typeof MOTIVOS)[number]) ? motivo : 'outro'}`));
    const prox = new URLSearchParams(params);
    prox.delete('google');
    prox.delete('motivo');
    setParams(prox, { replace: true });
    void qc.invalidateQueries({ queryKey: CHAVE_MINHA_AGENDA });
    // Só na chegada com o parâmetro.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [volta]);

  const conectar = useMutation({
    mutationFn: () => post<{ url: string }>('/agenda/google/minha-conta/conectar', {}),
    onSuccess: (r) => window.location.assign(r.url),
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const criarProfissional = useMutation({
    mutationFn: () => post<{ tem_profissional: boolean }>('/agenda/google/minha-conta/profissional', {}),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: CHAVE_MINHA_AGENDA });
      toast.sucesso(t('minhaConta.profissionalCriado'));
    },
    // rgs-4: a recusa pode ser porque a conexão caiu com a tela aberta; o cartão relê o estado.
    onError: (e) => {
      toast.erro(mensagemDeErro(e));
      void qc.invalidateQueries({ queryKey: CHAVE_MINHA_AGENDA });
    },
  });
  const desconectar = useMutation({
    mutationFn: () => post<{ desconectado: true; revogado: boolean }>('/agenda/google/minha-conta/desconectar', {}),
    onSuccess: async () => {
      setConfirmando(false);
      await qc.invalidateQueries({ queryKey: [CHAVE] });
      toast.sucesso(t('minhaConta.desconectado'));
    },
    onError: (e) => (setConfirmando(false), toast.erro(mensagemDeErro(e))),
  });

  const d = q.data;
  const conectado = d?.estado === 'conectado';
  return (
    <Cartao titulo={t('minhaConta.titulo')} acao={d && <Selo cor={conectado ? 'verde' : 'neutro'}>{t(conectado ? 'minhaConta.estadoConectado' : 'minhaConta.estadoDesconectado')}</Selo>}>
      <div className="flex min-w-0 flex-col gap-3 text-sm" data-minha-agenda-google>
        {q.isPending ? (
          <p className="text-tinta-3">{t('minhaConta.carregando')}</p>
        ) : q.isError ? (
          <p className="text-perigo" role="alert">
            {mensagemDeErro(q.error)}
          </p>
        ) : (
          <>
            {(!conectado || d!.recursos.length > 0) && <p className="text-tinta-3">{t(conectado ? 'minhaConta.textoConectado' : 'minhaConta.texto')}</p>}
            {!conectado && d!.motivo === 'revogado' && (
              <p className="text-perigo" role="status" data-google-revogado>
                {t('minhaConta.revogado')}
              </p>
            )}
            {!d!.disponivel && <p className="text-tinta-3">{t('minhaConta.indisponivel')}</p>}
            {conectado && d!.falta_cor && d!.disponivel && !emSimulacao && <p className="text-tinta-2" role="status" data-google-falta-cor>{t('minhaConta.faltaCor')}</p>}
            {conectado && (
              <dl className="grid min-w-0 gap-2">
                <div className="min-w-0">
                  <dt className="font-medium text-tinta-2">{t('minhaConta.conta')}</dt>
                  <dd className="numero break-all text-tinta-1">{d!.conta ? anunciarOcultos(email(d!.conta)) : t('minhaConta.contaPendente')}</dd>
                </div>
                {(d!.recursos.length > 0 || !d!.tem_profissional) && (
                  <div className="min-w-0">
                    <dt className="font-medium text-tinta-2">{t('minhaConta.profissionais')}</dt>
                    <dd className="text-tinta-1">{d!.recursos.length ? d!.recursos.map((r) => r.nome).join(', ') : t('minhaConta.semProfissional')}</dd>
                  </div>
                )}
                <div className="min-w-0">
                  <dt className="font-medium text-tinta-2">{t('minhaConta.sincronia')}</dt>
                  <dd className="text-tinta-1">
                    {d!.recuo_ate ? t('minhaConta.recuo', { quando: dataHora(d!.recuo_ate) }) : d!.sincronizado_em ? dataHora(d!.sincronizado_em) : t('minhaConta.aguardando')}
                  </dd>
                </div>
              </dl>
            )}
            {!conectado && !d!.tem_profissional && <p className="text-tinta-3">{t('minhaConta.semProfissionalTexto')}</p>}
            {conectado && !d!.tem_profissional && !emSimulacao && (
              <div className="flex min-w-0 flex-col items-start gap-2" data-criar-profissional>
                <p className="text-tinta-3">{t('minhaConta.criarProfissionalTexto')}</p>
                <Botao variante="secundario" onClick={() => criarProfissional.mutate()} carregando={criarProfissional.isPending}>
                  {t('minhaConta.criarProfissional')}
                </Botao>
              </div>
            )}
            {d!.tem_profissional && d!.recursos.length === 0 && <p className="text-tinta-3">{t('minhaConta.profissionalIndisponivel')}</p>}
            {d!.tem_profissional && s.pode('agenda.ver') && <HistoricoDeAgendamentos />}
            {!emSimulacao && (
              <div className="flex flex-wrap justify-end gap-2">
                {conectado && d!.falta_cor && d!.disponivel && (
                  <Botao onClick={() => conectar.mutate()} carregando={conectar.isPending} data-google-reconectar>
                    {t('minhaConta.reconectar')}
                  </Botao>
                )}
                {conectado ? (
                  <Botao onClick={() => setConfirmando(true)} carregando={desconectar.isPending} data-google-desconectar>
                    {t('minhaConta.desconectar')}
                  </Botao>
                ) : (
                  <Botao icone={<CalendarCheck className="size-4" aria-hidden />} onClick={() => conectar.mutate()} carregando={conectar.isPending} disabled={!d!.disponivel} className="max-w-full whitespace-normal" data-google-conectar>
                    {t('minhaConta.conectar')}
                  </Botao>
                )}
              </div>
            )}
          </>
        )}
      </div>
      <Confirmar
        aberto={confirmando}
        aoFechar={() => {
          setConfirmando(false);
          // Cancelar devolve o foco ao botão que abriu.
          document.querySelector<HTMLButtonElement>('[data-google-desconectar]')?.focus();
        }}
        aoConfirmar={() => desconectar.mutate()}
        titulo={t('minhaConta.confirmarTitulo')}
        texto={t('minhaConta.confirmarTexto')}
        perigoso
        carregando={desconectar.isPending}
      />
    </Cartao>
  );
}
